import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Privacy-first, self-hosted page-view beacon.
 * — no cookies, no localStorage identifier, no third-party requests
 * — sends { path, ref_host, viewport_w, lang } to /api/beacon
 * — errors are swallowed silently (never affect UX)
 *
 * Mount ONCE at the top level of your route tree.
 */
export const AnalyticsBeacon = () => {
  const location = useLocation();
  const lastPath = useRef(null);
  const debouncer = useRef(null);

  useEffect(() => {
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;

    // Debounce to avoid double-firing during rapid client-side redirects
    if (debouncer.current) clearTimeout(debouncer.current);
    debouncer.current = setTimeout(() => {
      const refHost = (() => {
        try { return document.referrer ? new URL(document.referrer).host : null; }
        catch { return null; }
      })();
      const payload = {
        path: location.pathname,
        ref_host: refHost && refHost !== window.location.host ? refHost : null,
        viewport_w: window.innerWidth || null,
        lang: (navigator.language || navigator.userLanguage || "en").slice(0, 8),
      };
      // Fire-and-forget: never block, never surface errors to user
      api.post("/beacon", payload).catch(() => null);
    }, 250);
    return () => { if (debouncer.current) clearTimeout(debouncer.current); };
  }, [location.pathname]);

  return null;
};
